import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { useNavigate } from "react-router-dom";
import _ from "lodash";

import Error from "../features/Error.jsx";
import Button from "../components/Button/Button.jsx";

import ButtonsGroup from "../components/ButtonsGroup/ButtonsGroup.jsx";
import ShimmerMultiLine from "../components/Shimmer/ShimmerMultiLine.jsx";

import WrapperContainer from "../components/WrapperContainer/WrapperContainer.jsx";
import { APP_ROUTES } from "../router";

import getContractAddress from "../utils/helpers/getContractAddress.js";
import handleNotify from "../utils/helpers/handleNotify.js";

import usePayment from "../hooks/usePayment.js";
import { apiAuthByCustomerKey } from "../api/methods/auth.js";

import { apiFetchContract, apiFetchContractBillingIds } from "../api/methods/contract.js";
import {
  apiFetchStatusProcess,
  apiFetchProcess,
  apiChangeProcess,
} from "../api/methods/process.js";
import useRedirect from "../hooks/useRedirect.js";

import UserPersonalParamList from "../components/UserPersonalParamList/UserPersonalParamList";
import UserPersonalParamItem from "../components/UserPersonalParamItem/UserPersonalParamItem";

import Alert from "../components/Alert/Alert";
import actions from "../store/actions.js";

const Payment = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const { getPaymentTinkoffUrl, handlePaymentDefault } = usePayment();

  const redirect = useRedirect();
  const { mobileConnectionInfo } = useSelector(state => state);

  const [contract, setContract] = useState({});
  const [sumPayment, setSumPayment] = useState(null);

  // Специальный трюк для изменения суммы оплаты (нужно для тестеров)
  const [counterClickedOnInput, setCounterClickedOnInput] = useState(0);
  const [hasError, setHasError] = useState(false);

  const isProgramAlpha = localStorage.getItem("partnerProgram") === "alpha";

  useEffect(() => {
    if (!mobileConnectionInfo.apiKey) {
      history(APP_ROUTES.MOBILE_CONNECTION.ROOT_ROUTE, { replace: true });
      return;
    }

    (async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has("source")) {
          await handlePayment();
        } else {
          await setTotalSum();

          const contractInfo = await apiFetchContractBillingIds(mobileConnectionInfo.apiKey);
          const tokenInfo = await apiAuthByCustomerKey(mobileConnectionInfo.apiKey);

          dispatch(actions.tokens.changeTokensInfo(tokenInfo));
          dispatch(actions.auth.changeAuthStatus(true));
          dispatch(actions.contract.changeContractInfo(contractInfo));

          const response = await apiFetchContract({
            contracts: [
              { contract_id: contractInfo.contract_id, billing_id: contractInfo.billing_id },
            ],
          });

          setContract(response.data?.detail[0]);
        }
      } catch {
        setHasError(true);
      }
    })();
  }, []);

  const setTotalSum = async () => {
    const { costConnection, costServices } = await apiFetchProcess(mobileConnectionInfo.apiKey, [
      "cost_connection",
      "cost_services",
    ]);

    setSumPayment((costConnection || 0) + (costServices || 0));
  };

  const handlePayment = async () => {
    const { isPaidSuccess } = handlePaymentDefault();

    if (isPaidSuccess) {
      await apiChangeProcess(mobileConnectionInfo.apiKey, {
        has_payment: true,
      });

      const processStatus = await apiFetchStatusProcess(mobileConnectionInfo.apiKey);
      redirect(processStatus);
    } else {
      history(APP_ROUTES.MOBILE_CONNECTION.PAYMENT_ROUTE, { replace: true });
    }
  };

  /**
   * Значение в поле ввода станет равным 1 рублю после 20 нажатий на него.
   * Специальная функция для возможности протестировать оплату
   */
  const handleClickOnInput = () => {
    if (counterClickedOnInput < 20) {
      setCounterClickedOnInput(prevState => prevState + 1);
    } else {
      setSumPayment(1);
    }
  };

  const handleSkipPayment = async () => {
    try {
      await apiChangeProcess(mobileConnectionInfo.apiKey, {
        has_payment: false,
      });

      const processStatus = await apiFetchStatusProcess(mobileConnectionInfo.apiKey);
      redirect(processStatus);
    } catch (error) {
      handleNotify("error", error);
    }
  };

  const handleClickPay = () => {
    const tag = isProgramAlpha ? "CASHBACK500PROMO" : "";
    getPaymentTinkoffUrl(contract_id, billing_id, sumPayment, "mobile", tag);
  };

  const { contract_title, customer_name, contract_address, contract_id, billing_id } = contract;

  if (hasError) return <Error />;
  if (_.isEmpty(contract)) return <ShimmerMultiLine count={2} />;

  return (
    <>
      <WrapperContainer gap="16" title="sm">
        Оплата
      </WrapperContainer>
      <WrapperContainer gap="24" text="lg">
        Номер договора и пароль отправлены вам в SMS на номер{" "}
        <b>+{mobileConnectionInfo.phoneNumber}</b>. Ваш договор активируется после оплаты и
        подписания документов.
      </WrapperContainer>

      {isProgramAlpha && (
        <WrapperContainer gap="24">
          <Alert type="warn">Для начисления кешбэка, необходимо оплатить картой Альфабанк</Alert>
        </WrapperContainer>
      )}

      <WrapperContainer gap="24">
        <UserPersonalParamList>
          <UserPersonalParamItem title="Единый лиц. счет" value={contract_title} />
          <UserPersonalParamItem title="ФИО владельца" value={customer_name} />

          {contract_address && (
            <UserPersonalParamItem title="По адресу" value={getContractAddress(contract_address)} />
          )}
        </UserPersonalParamList>
      </WrapperContainer>

      <WrapperContainer gap="24">
        <div className="total-cost">
          <div className="total-cost__label">К оплате:</div>
          <div className="total-cost__value">
            <div className="total-cost__cost" onClick={handleClickOnInput}>
              {sumPayment} ₽
            </div>
          </div>
        </div>
      </WrapperContainer>

      <ButtonsGroup>
        {!isProgramAlpha && (
          <Button isStretch color="secondary" size="xl" onClick={handleSkipPayment}>
            Оплатить позже
          </Button>
        )}
        <Button isStretch color="primary" size="xl" onClick={handleClickPay}>
          Оплатить
        </Button>
      </ButtonsGroup>
    </>
  );
};

export default Payment;
